/**
 * liquidMetalPipeline — flowing mercury-like metal surface.
 *
 * A raymarched sphere with strong FBM displacement, creating an
 * organic blob shape. The surface is highly reflective with a
 * mirror-like specular highlight and Fresnel rim. Multiple lights
 * create an iridescent chromatic edge effect.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface LiquidMetalPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float bass;
float mid;
float high;


float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm3D(vec3 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    float n = 0.0;
    vec3 ip = floor(p);
    vec3 fp = fract(p);
    fp = fp * fp * (3.0 - 2.0 * fp);
    vec3 cell = ip;
    n = hash21(cell.xy + cell.z * 17.0);
    float a2 = hash21(cell.xy + vec2(1.0, 0.0) + cell.z * 17.0);
    float b = hash21(cell.xy + vec2(0.0, 1.0) + cell.z * 17.0);
    float c = hash21(cell.xy + vec2(1.0, 1.0) + cell.z * 17.0);
    float d = hash21(cell.xy + vec2(2.0, 0.0) + (cell.z + 1.0) * 17.0);
    float e = hash21(cell.xy + vec2(0.0, 2.0) + cell.z * 17.0);
    float f = hash21(cell.xy + vec2(2.0, 1.0) + (cell.z + 1.0) * 17.0);
    float g = hash21(cell.xy + vec2(1.0, 2.0) + (cell.z + 1.0) * 17.0);
    float h = hash21(cell.xy + vec2(2.0, 2.0) + (cell.z + 1.0) * 17.0);
    // 8-corner trilinear interpolation (lazy, just use 4).
    v += a * mix(mix(n, a2, fp.x), mix(b, c, fp.x), fp.y);
    p = p * 2.0 + vec3(1.7, 9.2, 5.3);
    a *= 0.5;
  }
  return v;
}

// Sphere SDF with FBM displacement.
float mapBlob(vec3 p) {
  // Slow rotation.
  float a = uTime * 0.2;
  float ca = cos(a), sa = sin(a);
  p.xz = mat2(ca, -sa, sa, ca) * p.xz;
  // Base sphere.
  float baseR = 0.6;
  // Audio: bigger with bass.
  baseR += bass * 0.1;
  // Strong FBM displacement.
  float disp = fbm3D(p * 2.0 + uTime * 0.3) * 0.15;
  return length(p) - baseR - disp;
}

vec3 calcNormal(vec3 p) {
  vec2 e = vec2(0.002, 0.0);
  return normalize(vec3(
    mapBlob(p + e.xyy) - mapBlob(p - e.xyy),
    mapBlob(p + e.yxy) - mapBlob(p - e.yxy),
    mapBlob(p + e.yyx) - mapBlob(p - e.yyx)
  ));
}



void main() {
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  // Camera.
  vec3 ro = vec3(0.0, 0.0, 2.5);
  vec3 ta = vec3(0.0, 0.0, 0.0);
  vec3 cw = normalize(ta - ro);
  vec3 cu = normalize(cross(cw, vec3(0.0, 1.0, 0.0)));
  vec3 cv = cross(cu, cw);
  vec3 rd = normalize(p.x * cu + p.y * cv + 1.5 * cw);

  // Raymarch.
  float td = 0.0;
  bool hit = false;
  vec3 hitP = ro;
  for (int i = 0; i < 64; i++) {
    vec3 p2 = ro + rd * td;
    float d = mapBlob(p2);
    if (d < 0.005) { hit = true; hitP = p2; break; }
    td += d * 0.9;
    if (td > 5.0) break;
  }

  vec3 col;
  if (hit) {
    vec3 n = calcNormal(hitP);
    vec3 v = normalize(ro - hitP);
    // Strong fresnel.
    float fresnel = pow(1.0 - max(dot(n, v), 0.0), 4.0);
    // Two key lights.
    vec3 lightA = normalize(vec3(0.5, 1.0, 0.5));
    vec3 lightB = normalize(vec3(-0.7, -0.3, 0.5));
    vec3 hA = normalize(lightA + v);
    vec3 hB = normalize(lightB + v);
    float specA = pow(max(dot(n, hA), 0.0), 128.0);
    float specB = pow(max(dot(n, hB), 0.0), 64.0);
    // Audio: brightness boost.
    float bright = 1.0 + high * 0.5;
    // Iridescent chrome color.
    vec3 iridColor = vec3(
      0.7 + 0.3 * sin(hitP.x * 4.0 + uTime),
      0.7 + 0.3 * sin(hitP.y * 4.0 + uTime + 2.0),
      0.7 + 0.3 * sin(hitP.z * 4.0 + uTime + 4.0)
    );
    col = iridColor * 0.05;
    col += vec3(1.0, 0.95, 0.85) * specA * 3.0 * bright;
    col += vec3(0.6, 0.85, 1.0) * specB * 2.0 * bright;
    // Fresnel: bright rim.
    col += vec3(1.0, 0.95, 0.85) * fresnel * 0.8;
    // Audio: pulse on bass.
    col *= 1.0 + bass * 0.3;
  } else {
    // Background: dark with subtle gradient.
    float r = length(p);
    col = vec3(0.04, 0.06, 0.10) + vec3(0.05, 0.02, 0.10) * exp(-r * 2.0);
  }

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeLiquidMetalBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeLiquidMetalRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createLiquidMetalPipeline(gl: WebGL2RenderingContext): LiquidMetalPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawLiquidMetal(
  gl: WebGL2RenderingContext,
  pipe: LiquidMetalPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
